import type {CreateConnectionDataADto} from '@cohort/admin-schemas/connection';
import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import Input from '@cohort/merchants/components/form/v2/Input';
import UrlInput from '@cohort/merchants/components/form/v2/UrlInput';
import Header from '@cohort/merchants/components/Header';
import {connectionsKeys} from '@cohort/merchants/hooks/api/Connections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {createConnection} from '@cohort/merchants/lib/api/Connections';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {urlTransformer, urlValidator} from '@cohort/shared/utils/validators';
import {zodResolver} from '@hookform/resolvers/zod';
import {ArrowSquareOut} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

const API_KEY_DOCUMENTATION_LINK =
  'https://www.notion.so/cohort-xyz/Get-your-API-Key-50f71e3d0f1542ff9f9c60c81027bfe8';

const PrestashopConnectionSchema = z.object({
  shopUrl: z
    .string()
    .refine(urlValidator, 'errorUrl')
    .transform(url => urlTransformer(url)),
  apiKey: z.string().min(1, {message: 'errorRequired'}),
  useSecureConnection: z.boolean().default(true),
});
type PrestashopConnectionType = z.infer<typeof PrestashopConnectionSchema>;

const PrestashopEditConnection: React.FC<ConnectionEditComponentProps> = ({onClose}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const queryClient = useQueryClient();
  const {t} = useTranslation('app-prestashop', {
    keyPrefix: 'editConnection',
  });
  const {register, handleSubmit, control} = useForm<PrestashopConnectionType>({
    resolver: zodResolver(PrestashopConnectionSchema),
  });

  const {isLoading, mutate: createConnectionMutation} = useCohortMutation({
    mutationFn: async (data: CreateConnectionDataADto) => createConnection(merchantId, data),
    notifySuccessMessage: t('createSuccessNotification'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionsKeys.list(merchantId));
      onClose();
    },
    onError: err => notifyError(err, t('errorInvalidCredentials')),
  });

  return (
    <form
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(async data => {
          createConnectionMutation({
            connectorId: 'prestashop',
            name: null,
            credentials: {
              shopUrl: data.shopUrl,
              apiKey: data.apiKey,
              useSecureConnection: data.useSecureConnection,
            },
          });
        })(e);
      }}
      className="space-y-6 p-6"
      data-testid="create-connection-form"
      id="create-connection-form"
    >
      <Header title={t('title')} subtitle={t('subtitle')} />
      <UrlInput
        type="text"
        name="shopUrl"
        label={t('labelShopUrl')}
        description={t('descriptionShopUrl')}
        register={register}
        control={control}
      />
      <Input
        type="text"
        name="apiKey"
        label={t('labelApiKey')}
        register={register}
        control={control}
        description={
          <a
            className="flex text-primary"
            href={API_KEY_DOCUMENTATION_LINK}
            target="_blank"
            rel="noreferrer"
          >
            <ArrowSquareOut size={20} className="mr-2" />
            {t('labelApiKeyDocumentation')}
          </a>
        }
      />
      <Input
        type="checkbox"
        name="useSecureConnection"
        label={t('labelUseSecureConnection')}
        register={register}
        control={control}
        defaultChecked={true}
      />
      <Button
        data-testid="create-digital-asset-collection-submit-btn"
        type="submit"
        form="create-connection-form"
        loading={isLoading}
      >
        {t('buttonCreate')}
      </Button>
    </form>
  );
};

export default PrestashopEditConnection;
